import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'components/modal';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from 'flowbite-react';
import { motion } from 'framer-motion';

const DEFAULT_OPTIONS = {
  allowOutsideClick: true,
  cancelButtonText: 'Batal',
  confirmButtonText: 'OK',
  denyButtonText: 'Tidak',
  icon: 'info',
  showCancelButton: false,
  showConfirmButton: true,
  showDenyButton: false,
};

const ICONS = {
  success: {
    Icon: CheckCircleIcon,
    accentClassName: 'from-emerald-400 via-teal-400 to-cyan-400',
    glowClassName: 'bg-emerald-400/20',
    iconClassName: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    confirmClassName:
      'bg-gradient-to-r from-emerald-500 to-teal-500 shadow-emerald-500/25 hover:from-emerald-600 hover:to-teal-600 focus:ring-emerald-200',
  },
  error: {
    Icon: XCircleIcon,
    accentClassName: 'from-rose-400 via-red-400 to-orange-400',
    glowClassName: 'bg-rose-400/20',
    iconClassName: 'bg-rose-50 text-rose-600 ring-rose-100',
    confirmClassName:
      'bg-gradient-to-r from-rose-500 to-red-500 shadow-rose-500/25 hover:from-rose-600 hover:to-red-600 focus:ring-rose-200',
  },
  warning: {
    Icon: ExclamationTriangleIcon,
    accentClassName: 'from-amber-300 via-orange-400 to-rose-400',
    glowClassName: 'bg-amber-400/20',
    iconClassName: 'bg-amber-50 text-amber-600 ring-amber-100',
    confirmClassName:
      'bg-gradient-to-r from-amber-500 to-orange-500 shadow-orange-500/25 hover:from-amber-600 hover:to-orange-600 focus:ring-amber-200',
  },
  info: {
    Icon: InformationCircleIcon,
    accentClassName: 'from-sky-400 via-blue-400 to-indigo-400',
    glowClassName: 'bg-sky-400/20',
    iconClassName: 'bg-sky-50 text-sky-600 ring-sky-100',
    confirmClassName:
      'bg-gradient-to-r from-sky-500 to-blue-600 shadow-blue-500/25 hover:from-sky-600 hover:to-blue-700 focus:ring-sky-200',
  },
  question: {
    Icon: QuestionMarkCircleIcon,
    accentClassName: 'from-violet-400 via-indigo-400 to-blue-400',
    glowClassName: 'bg-violet-400/20',
    iconClassName: 'bg-violet-50 text-violet-600 ring-violet-100',
    confirmClassName:
      'bg-gradient-to-r from-violet-500 to-indigo-600 shadow-indigo-500/25 hover:from-violet-600 hover:to-indigo-700 focus:ring-violet-200',
  },
};

const subscribers = new Set();
let requestId = 0;

const normalizeOptions = (titleOrOptions, text, icon) => {
  if (typeof titleOrOptions === 'object' && titleOrOptions !== null) {
    return {
      ...DEFAULT_OPTIONS,
      ...titleOrOptions,
      text: titleOrOptions.text ?? titleOrOptions.html,
    };
  }

  // Mendukung bentuk ringkas lama: fire('Pesan', 'success').
  if (icon === undefined && ICONS[text]) {
    return { ...DEFAULT_OPTIONS, title: titleOrOptions, icon: text };
  }

  return {
    ...DEFAULT_OPTIONS,
    title: titleOrOptions,
    text,
    icon: icon || DEFAULT_OPTIONS.icon,
  };
};

const fire = (...args) =>
  new Promise((resolve) => {
    const request = { id: ++requestId, options: normalizeOptions(...args), resolve };

    if (subscribers.size === 0) {
      resolve({ isConfirmed: false, isDenied: false, isDismissed: true, dismiss: 'unavailable' });
      return;
    }

    subscribers.forEach((subscriber) => subscriber(request));
  });

const alertDialog = { fire };

const AlertDialogProvider = () => {
  const [queue, setQueue] = useState([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const inputRef = useRef(null);
  const dialogRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();
  const activeRequest = queue[0];
  const options = activeRequest?.options;
  const didOpen = options?.didOpen;

  useEffect(() => {
    const subscribe = (request) => setQueue((currentQueue) => [...currentQueue, request]);
    subscribers.add(subscribe);
    return () => subscribers.delete(subscribe);
  }, []);

  const finish = useCallback(
    (result) => {
      if (!activeRequest) return;
      activeRequest.resolve(result);
      setIsConfirming(false);
      setQueue((currentQueue) => currentQueue.slice(1));
    },
    [activeRequest]
  );

  useEffect(() => {
    if (!options?.timer) return undefined;

    const timer = window.setTimeout(() => {
      finish({
        isConfirmed: false,
        isDenied: false,
        isDismissed: true,
        dismiss: 'timer',
      });
    }, options.timer);

    return () => window.clearTimeout(timer);
  }, [finish, options?.timer]);

  useEffect(() => {
    if (!activeRequest || !didOpen) return undefined;
    const frame = window.requestAnimationFrame(() => didOpen());
    return () => window.cancelAnimationFrame(frame);
  }, [activeRequest, didOpen]);

  const dismiss = () => {
    if (isConfirming || options?.allowOutsideClick === false) return;
    cancel();
  };

  const cancel = () => {
    if (isConfirming) return;
    finish({
      isConfirmed: false,
      isDenied: false,
      isDismissed: true,
      dismiss: 'cancel',
    });
  };

  useEffect(() => {
    if (!activeRequest) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && options?.allowEscapeKey !== false && !isConfirming) {
        finish({
          isConfirmed: false,
          isDenied: false,
          isDismissed: true,
          dismiss: 'escape',
        });
        return;
      }

      if (event.key !== 'Tab') return;
      const focusableElements = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements?.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeRequest, finish, isConfirming, options?.allowEscapeKey]);

  useEffect(() => {
    if (!activeRequest || options?.input) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const preferredTarget = options?.showCancelButton
        ? '[data-dialog-cancel]'
        : options?.showConfirmButton !== false
          ? '[data-dialog-confirm]'
          : '[data-dialog-close]';
      dialogRef.current?.querySelector(preferredTarget)?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeRequest, options?.input, options?.showCancelButton, options?.showConfirmButton]);

  const confirm = async () => {
    if (!activeRequest || isConfirming) return;

    setIsConfirming(true);
    try {
      const inputValue = inputRef.current?.value ?? options.inputValue ?? '';
      const preConfirmResult = options.preConfirm
        ? await options.preConfirm(inputValue)
        : undefined;
      if (preConfirmResult === false) {
        setIsConfirming(false);
        return;
      }
      finish({
        isConfirmed: true,
        isDenied: false,
        isDismissed: false,
        value: preConfirmResult ?? (options.input ? inputValue : true),
      });
    } catch (error) {
      setIsConfirming(false);
      console.error('AlertDialog preConfirm gagal:', error);
    }
  };

  const deny = () => {
    const inputValue = inputRef.current?.value ?? options.inputValue ?? '';
    finish({
      isConfirmed: false,
      isDenied: true,
      isDismissed: false,
      value: options.input ? inputValue : false,
    });
  };

  if (!activeRequest) return null;

  const iconConfig = ICONS[options.icon] || ICONS.info;
  const { Icon } = iconConfig;

  return (
    <Modal
      key={activeRequest.id}
      ref={dialogRef}
      show
      size='md'
      aria-describedby={options.text ? descriptionId : undefined}
      className='z-[100]'
      onClose={cancel}
      dismissible={false}
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          event.target.dataset.testid === 'modal-overlay'
        ) {
          dismiss();
        }
      }}
    >
      <ModalHeader>
        <span id={titleId}>{options.title || 'Informasi'}</span>
        {options.allowOutsideClick !== false && (
          <button
            type='button'
            aria-label='Tutup dialog'
            data-dialog-close
            onClick={cancel}
            disabled={isConfirming}
            className='absolute right-5 top-4 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          >
            <XMarkIcon className='h-5 w-5' aria-hidden='true' />
          </button>
        )}
      </ModalHeader>
      <ModalBody>
        <div className='flex flex-col items-center gap-4 text-center'>
          <div
            className={
              'flex h-14 w-14 items-center justify-center rounded-xl ' + iconConfig.iconClassName
            }
          >
            <Icon aria-hidden='true' className='h-8 w-8' />
          </div>
          {options.text && (
            <p id={descriptionId} className='whitespace-pre-line text-sm leading-6 text-gray-600'>
              {options.text}
            </p>
          )}
          {options.input && (
            <input
              key={activeRequest.id}
              ref={inputRef}
              autoFocus
              className='form-input w-full rounded-lg border border-stroke px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
              defaultValue={options.inputValue ?? ''}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  confirm();
                }
              }}
              placeholder={options.inputPlaceholder}
              type={options.input === 'textarea' ? 'text' : options.input}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {options.showCancelButton && (
          <button
            type='button'
            className={'modal-action-neutral ' + (options.customClass?.cancelButton || '')}
            data-dialog-cancel
            disabled={isConfirming}
            onClick={cancel}
          >
            {options.cancelButtonText}
          </button>
        )}
        {options.showDenyButton && (
          <button
            type='button'
            className={
              'rounded-lg bg-red-600 text-white hover:bg-red-700 ' +
              (options.customClass?.denyButton || '')
            }
            disabled={isConfirming}
            onClick={deny}
          >
            {options.denyButtonText}
          </button>
        )}
        {options.showConfirmButton && (
          <button
            type='button'
            className={
              'inline-flex items-center gap-2 rounded-lg bg-primary text-white hover:bg-primary-dark ' +
              (options.customClass?.confirmButton || options.confirmButtonClassName || '')
            }
            data-dialog-confirm
            disabled={isConfirming}
            onClick={confirm}
            style={
              options.confirmButtonColor
                ? { backgroundColor: options.confirmButtonColor }
                : undefined
            }
          >
            {isConfirming && <Spinner aria-label='Memproses' size='sm' />}
            {options.confirmButtonText}
          </button>
        )}
      </ModalFooter>
      {options.timer && (
        <div className='h-1 overflow-hidden rounded-b-2xl bg-gray-100'>
          <motion.div
            animate={{ scaleX: 0 }}
            className={'h-full origin-left bg-gradient-to-r ' + iconConfig.accentClassName}
            initial={{ scaleX: 1 }}
            transition={{ duration: options.timer / 1000, ease: 'linear' }}
          />
        </div>
      )}
    </Modal>
  );
};

export { AlertDialogProvider };
export default alertDialog;
